import {
    Component,
    OnInit,
    ViewChild,
    inject,
    signal,
    computed,
    DestroyRef,
    ViewEncapsulation,
    effect,
    ViewChildren,
    QueryList,
} from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute } from "@angular/router";
import { DocumentManagerService } from "@app/controls/testing/advanced/detail-new/common/services/document-manager.service";
import {
    ProcessedDocumentDetailModel,
    ProcessedDocumentWorkflowStatus,
} from "@app/document-intelligence/models/ProcessedDocumentDetailModel";
import { ProcessedDocumentRiskModel } from "@app/document-intelligence/models/ProcessedDocumentRiskModel";
import { ProcessedDocumentsService } from "@app/document-intelligence/services/processed-documents-service";
import { CerrixSlideOverComponent } from "@app/shared/cerrix-slide-over/cerrix-slide-over.component";
import { WorkflowStepItemModel } from "@app/shared/cerrix-workflow-bar/models/workflow-step-item";
import { WorkflowStepStatus } from "@app/shared/cerrix-workflow-bar/models/workflow-step-status.enum";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ProcessedDocumentDocumentModel } from "@app/document-intelligence/models/ProcessedDocumentDocumentModel";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { CerrixInputGroupComponent } from "@app/shared/molecules/cerrix-input-group/cerrix-input-group.component";
import { CerrixTextareaGroupComponent } from "@app/shared/molecules/cerrix-textarea-group/cerrix-textarea-group.component";
import { ToastrService } from "ngx-toastr";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { ProcessedDocumentSuggestionModel } from "@app/document-intelligence/models/ProcessedDocumentSuggestionModel";
import { RiskConfigModel } from "@app/risk/models/risk-config-model";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { ControlConfigModel } from "@app/controls/models/ControlConfigModel";
import { firstValueFrom } from "rxjs";
import { CerrixWorkflowBarComponent } from "@app/shared/cerrix-workflow-bar/cerrix-workflow-bar.component";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixPaginationComponent } from "@app/shared/molecules/cerrix-pagination/cerrix-pagination.component";
import { CerrixShowTotalItemsComponent } from "@app/shared/molecules/cerrix-show-total-items/cerrix-show-total-items.component";
import { CerrixAcceptRejectToggleComponent } from "@app/shared/molecules/cerrix-accept-reject-toggle/cerrix-accept-reject-toggle.component";
import { CerrixBannerComponent } from "@app/shared/atoms/cerrix-banner/cerrix-banner.component";
import { CerrixPageFooterComponent } from "@app/shared/cerrix-page-footer/cerrix-page-footer.component";
import { CerrixConfirmDialogComponent } from "@app/shared/organisms/cerrix-confirm-dialog/cerrix-confirm-dialog.component";
import { CerrixIconButtonComponent } from "@app/shared/atoms/cerrix-icon-button/cerrix-icon-button.component";

@Component({
    selector: "risk-generate-ai",
    templateUrl: "./risk-generate-ai.component.html",
    styleUrl: "./risk-generate-ai.component.scss",
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgTemplateOutlet,
        MatIconModule,
        CerrixWorkflowBarComponent,
        CerrixInstructionsIconComponent,
        CerrixSlideOverComponent,
        CerrixButtonComponent,
        CerrixPaginationComponent,
        CerrixShowTotalItemsComponent,
        CerrixAcceptRejectToggleComponent,
        CerrixIconButtonComponent,
        CerrixBannerComponent,
        CerrixInputGroupComponent,
        CerrixTextareaGroupComponent,
        CerrixPageFooterComponent,
        CerrixConfirmDialogComponent,
    ],
    providers: [CerrixInputValidationRegistryService],
})
export class RiskGenerateAiComponent implements OnInit {
    @ViewChild("cerrixSlideOver") cerrixSlideOver: CerrixSlideOverComponent;
    @ViewChildren(CerrixInputGroupComponent) inputFields!: QueryList<CerrixInputGroupComponent>;
    @ViewChildren(CerrixTextareaGroupComponent)
    textareaFields!: QueryList<CerrixTextareaGroupComponent>;

    // UI state
    public loading = true;
    public creaiLoadingMinutes = signal("");
    public currentStepId = signal("");
    public sourceDocumentsOpen = false;
    public sourceDocuments: ProcessedDocumentDocumentModel[] = [];
    public completedWithoutOutput = false;
    public currentPage = signal(1);
    private readonly previousPage = signal<number | null>(null);
    public itemsPerPage = signal(5);
    private readonly previousItemsPerPage = signal<number | null>(null);
    private readonly previousTracking = effect(() => {
        const currentPage = this.currentPage();
        const itemsPerPage = this.itemsPerPage();

        if (this.previousPage() !== currentPage) {
            this.previousPage.set(currentPage);
            this.openRiskContentIds.set(new Set());
            this.openControlContentIds.set(new Set());
        }

        if (this.previousItemsPerPage() !== itemsPerPage) {
            this.previousItemsPerPage.set(itemsPerPage);
            const visibleRiskIds = new Set(this.paginatedRisks().map((r) => r.id));
            this.openRiskContentIds.update((open) => {
                const next = new Set<number>();
                open.forEach((id) => {
                    if (visibleRiskIds.has(id)) {
                        next.add(id);
                    }
                });
                return next;
            });
            this.openControlContentIds.update((open) => {
                const next = new Set<number>();
                open.forEach((id) => {
                    const riskId = this.risks().find((r) =>
                        r.controls.some((c) => c.id === id),
                    )?.id;
                    if (riskId && visibleRiskIds.has(riskId)) {
                        next.add(id);
                    }
                });
                return next;
            });
        }
    });

    public savingGeneratedRisksControls = false;
    public generatedRisksControlsSaved = false;
    public updatingWorkflow = false;
    public workflowUpdated = false;

    protected workflowGenerate = "generateWithAi";
    protected workflowSelect = "createSelection";
    protected workflowRefine = "refineAndCreate";
    protected workflowComplete = "completed";
    public workflowSteps: WorkflowStepItemModel[] = [
        {
            id: this.workflowGenerate,
            name: "Generate with AI",
            status: WorkflowStepStatus.Active,
            infoTitle: "",
        },
        {
            id: this.workflowSelect,
            name: "Create Selection",
            status: WorkflowStepStatus.Disabled,
            infoTitle: "",
        },
        {
            id: this.workflowRefine,
            name: "Refine & Create",
            status: WorkflowStepStatus.Disabled,
            infoTitle: "",
        },
        {
            id: this.workflowComplete,
            name: "Completed",
            status: WorkflowStepStatus.Disabled,
            infoTitle: "",
        },
    ];

    public unselectedConfirmDialogName = "unselectedConfirm";
    public itemRejectedConfirmDialogName = "itemRejectedConfirm";
    public itemIncompleteConfirmDialogName = "itemIncompleteConfirm";

    public risks = signal<ProcessedDocumentRiskModel[]>([]);
    public openRiskIds = signal<Set<number>>(new Set());
    public openRiskContentIds = signal<Set<number>>(new Set());
    public openControlContentIds = signal<Set<number>>(new Set());
    public hasClosedRisks = computed(() => this.openRiskIds().size < this.risks().length);
    public processedDocument: ProcessedDocumentDetailModel;

    public risksCount = computed(() => this.counts().risksCount);
    public controlsCount = computed(() => this.counts().controlsCount);
    public finishedRisksCount = computed(() => this.counts().finishedRisksCount);
    public finishedControlsCount = computed(() => this.counts().finishedControlsCount);

    public paginatedRisks = computed(() => {
        const all = this.risks();
        const start = (this.currentPage() - 1) * this.itemsPerPage();
        const end = this.currentPage() * this.itemsPerPage();
        return all.slice(start, end);
    });

    private route = inject(ActivatedRoute);
    private _processedDocumentsService = inject(ProcessedDocumentsService);
    private _documentManagerService = inject(DocumentManagerService);
    private _dialogService = inject(CerrixDialogService);
    private _destroyRef = inject(DestroyRef);
    private _validationRegistry = inject(CerrixInputValidationRegistryService);
    private _toastrService = inject(ToastrService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _waitingTimeInterval: ReturnType<typeof setInterval>;

    private id: string;
    private tab?: TabModel;

    private counts = computed(() => {
        const risks = this.risks();
        let risksCount = 0;
        let controlsCount = 0;
        let finishedRisksCount = 0;
        let finishedControlsCount = 0;

        for (const risk of risks) {
            if (risk.selected) risksCount++;
            if (risk.isDoneProcessing) finishedRisksCount++;
            for (const control of risk.controls) {
                if (control.selected) controlsCount++;
                if (control.isDoneProcessing) finishedControlsCount++;
            }
        }
        return {
            risksCount,
            controlsCount,
            finishedRisksCount,
            finishedControlsCount,
        };
    });

    async ngOnInit(): Promise<void> {
        this.id = this.route.snapshot.params.id ? this.route.snapshot.params.id : this.tab.id;

        if (!this.id) {
            return;
        }

        const canAccess = await firstValueFrom(this._processedDocumentsService.canAccess());
        if (!canAccess) {
            this.tab.close(false);
            return;
        }

        await this.reloadProcessedDocument();

        this.tab.showLoader = false;
    }

    private async reloadProcessedDocument(): Promise<void> {
        const processedDocument = await this.loadProcessedDocument(+this.id);
        this.setWorkflow(processedDocument);
    }

    private setWorkflow(processedDocument: ProcessedDocumentDetailModel): void {
        if (processedDocument) {
            this.sourceDocuments = [processedDocument.document];
            this.loading = false;

            if (this.isCompletedWorkflowStatus(processedDocument.workflowStatus)) {
                this.processedDocument = processedDocument;
                this.setCompletedRisksAndControls(processedDocument);
                this.setWorkflowStep(this.workflowComplete);
                this.showAllControls();
                return;
            }

            if (processedDocument.workflowStatus == ProcessedDocumentWorkflowStatus.refining) {
                this.processedDocument = processedDocument;
                this.setSelectedRisksAndControls(processedDocument);
                this.setWorkflowStep(this.workflowRefine);
                this.showAllControls();
                return;
            }

            const remainingTime = this.calculateRemainingTime(processedDocument);

            if (remainingTime == "Finished Processing") {
                this.processedDocument = processedDocument;
                this.risks.set(this.processedDocument.risks);
                this.setWorkflowStep(this.workflowSelect);
                this.showAllControls();
            } else {
                this.setWorkflowStep(this.workflowGenerate);
                this.creaiLoadingMinutes.update((_) => remainingTime);

                this._waitingTimeInterval = setInterval(() => {
                    this.handleInterval();
                }, 60000);

                this._destroyRef.onDestroy(() => {
                    clearInterval(this._waitingTimeInterval);
                });
            }
        }
    }

    private setSelectedRisksAndControls(processedDocument: ProcessedDocumentDetailModel): void {
        const isSelectedValidationMethod = (item: ProcessedDocumentSuggestionModel) =>
            item.selected === true;

        this.setRisksAndControls(isSelectedValidationMethod, processedDocument);
    }

    private setCompletedRisksAndControls(processedDocument: ProcessedDocumentDetailModel): void {
        const isCompletedValidationMethod = (item: ProcessedDocumentSuggestionModel) =>
            item.isDoneProcessing === true;

        this.setRisksAndControls(isCompletedValidationMethod, processedDocument);
    }

    // This clobbers processed document, which might not be the best way to do this... But I doubt it will cause any issues
    private setRisksAndControls(
        isIncludedValidation: (item: ProcessedDocumentSuggestionModel) => boolean,
        processedDocument: ProcessedDocumentDetailModel,
    ): void {
        let unlinkedControlsRiskObject = new ProcessedDocumentRiskModel();
        unlinkedControlsRiskObject.id = -1;
        unlinkedControlsRiskObject.selected = false;
        unlinkedControlsRiskObject.hasLinkedRisks = false;
        unlinkedControlsRiskObject.controls = [];

        let linkedControlsRiskObject = new ProcessedDocumentRiskModel();
        linkedControlsRiskObject.id = -2;
        linkedControlsRiskObject.selected = false;
        linkedControlsRiskObject.hasLinkedRisks = true;
        linkedControlsRiskObject.controls = [];

        let result: ProcessedDocumentRiskModel[] = [];
        processedDocument.risks.forEach((r) => {
            if (isIncludedValidation(r)) {
                result.push(r);
                r.controls = r.controls.filter((c) => isIncludedValidation(c));
            } else {
                r.controls
                    .filter((c) => isIncludedValidation(c))
                    .forEach((c) =>
                        c.linkedRiskCount === 0
                            ? unlinkedControlsRiskObject.controls.push(c)
                            : linkedControlsRiskObject.controls.push(c),
                    );
            }
        });

        if (unlinkedControlsRiskObject.controls.length !== 0) {
            result.unshift(unlinkedControlsRiskObject); // Specifically add this to start to be shown first
        }

        if (linkedControlsRiskObject.controls.length !== 0) {
            result.push(linkedControlsRiskObject);
        }

        this.risks.set(result);
    }

    public isRiskOpen = (id: number) => this.openRiskIds().has(id);
    public isRiskContentOpen = (id: number) => this.openRiskContentIds().has(id);
    public isControlContentOpen = (id: number) => this.openControlContentIds().has(id);

    public showAllControls(): void {
        const allIds = new Set(this.risks().map((r) => r.id));
        this.openRiskIds.set(allIds);
    }

    public hideAllControls(): void {
        this.openRiskIds.set(new Set());
        this.openRiskContentIds.set(new Set());
        this.openControlContentIds.set(new Set());
    }

    public toggleRiskContent(id: number): void {
        if (this.currentStepId() !== this.workflowSelect) {
            return;
        }

        this.openRiskContentIds.update((currentOpenRiskContentIds) => {
            const updatedOpenRiskContentIds = new Set(currentOpenRiskContentIds);
            updatedOpenRiskContentIds.has(id)
                ? updatedOpenRiskContentIds.delete(id)
                : updatedOpenRiskContentIds.add(id);
            return updatedOpenRiskContentIds;
        });
    }

    public toggleControlContent(id: number): void {
        if (this.currentStepId() !== this.workflowSelect) {
            return;
        }

        this.openControlContentIds.update((currentOpenControlContentIds) => {
            const updatedOpenControlContentIds = new Set(currentOpenControlContentIds);
            updatedOpenControlContentIds.has(id)
                ? updatedOpenControlContentIds.delete(id)
                : updatedOpenControlContentIds.add(id);
            return updatedOpenControlContentIds;
        });
    }

    public toggleControlsVisibility(id: number): void {
        this.openRiskIds.update((currentOpenRiskIds) => {
            const updatedOpenRiskIds = new Set(currentOpenRiskIds);

            if (updatedOpenRiskIds.has(id)) {
                updatedOpenRiskIds.delete(id);

                this.openRiskContentIds.update((currentOpenRiskContentIds) => {
                    const updatedOpenRiskContentIds = new Set(currentOpenRiskContentIds);
                    updatedOpenRiskContentIds.delete(id);
                    return updatedOpenRiskContentIds;
                });

                this.openControlContentIds.update((currentOpenControlContentIds) => {
                    const updatedOpenControlContentIds = new Set(currentOpenControlContentIds);
                    const risk = this.risks().find((r) => r.id === id);
                    if (risk) {
                        risk.controls.forEach((control) =>
                            updatedOpenControlContentIds.delete(control.id),
                        );
                    }
                    return updatedOpenControlContentIds;
                });
            } else {
                updatedOpenRiskIds.add(id);
            }

            return updatedOpenRiskIds;
        });
    }

    public getChunkData(chunkId: number): string {
        const chunk = this.processedDocument.chunks.find((c) => c.id === chunkId);
        return chunk ? chunk.content : "Data not found";
    }

    public updateRiskSelected(riskId: number, selected: boolean | null) {
        this.risks.update((risks) =>
            risks.map((risk) => (risk.id === riskId ? { ...risk, selected } : risk)),
        );
    }

    public updateControlSelected(riskId: number, controlId: number, selected: boolean | null) {
        this.risks.update((risks) =>
            risks.map((risk) =>
                risk.id !== riskId
                    ? risk
                    : {
                          ...risk,
                          controls: risk.controls.map((control) =>
                              control.id === controlId ? { ...control, selected } : control,
                          ),
                      },
            ),
        );
    }

    public sourceDocumentDownload(documentGuid: string) {
        if (documentGuid) {
            this._documentManagerService.downloadDocumentByGuid(documentGuid);
        }
    }

    public async saveClick(): Promise<void> {
        if (!this.validateFields()) {
            return;
        }

        if (!this.savingGeneratedRisksControls) {
            try {
                this.savingGeneratedRisksControls = true;
                await this.save();
                this.generatedRisksControlsSaved = true;
                setTimeout(() => (this.generatedRisksControlsSaved = false), 2000);
            } finally {
                this.savingGeneratedRisksControls = false;
            }
        }
    }

    public async finishSelectionWorkflow(): Promise<void> {
        const risks = this.risks();
        const hasNull = risks.some(
            (risk) =>
                risk.selected === null ||
                risk.controls.some((control) => control.selected === null),
        );

        if (hasNull) {
            this.showUnselectedDialog();
            return;
        }

        let hasFalse = risks.some(
            (risk) =>
                risk.selected === false ||
                risk.controls.some((control) => control.selected === false),
        );

        if (hasFalse) {
            this.showItemRejectedDialog();
            return;
        }

        await this.handleUpdateWorkflow();
    }

    private showUnselectedDialog(): void {
        this._dialogService.openDialog(this.unselectedConfirmDialogName);
    }

    private showItemRejectedDialog(): void {
        this._dialogService.openDialog(this.itemRejectedConfirmDialogName);
    }

    public async finishCreationWorkflow(): Promise<void> {
        const risks = this.risks();

        const hasIncomplete = risks.some(
            (risk) =>
                risk.isDoneProcessing === false ||
                risk.controls.some((control) => control.isDoneProcessing === false),
        );

        if (hasIncomplete) {
            this.showItemIncompleteDialog();
            return;
        }

        await this.handleUpdateWorkflow();
    }

    private showItemIncompleteDialog(): void {
        this._dialogService.openDialog(this.itemIncompleteConfirmDialogName);
    }

    public async handleUpdateWorkflow(): Promise<void> {
        if (!this.validateFields()) {
            return;
        }

        if (!this.updatingWorkflow) {
            try {
                this.updatingWorkflow = true;
                if (this.currentStepId() === this.workflowSelect) {
                    await this.save();
                }
                await firstValueFrom(this._processedDocumentsService.advanceWorkflow(+this.id));
                this.closeConfirmDialogs();
                await this.reloadProcessedDocument();

                this.workflowUpdated = true;
                setTimeout(() => (this.workflowUpdated = false), 2000);
            } finally {
                this.updatingWorkflow = false;
            }
        }
    }

    public closeConfirmDialogs(): void {
        this._dialogService.closeDialog(this.unselectedConfirmDialogName);
        this._dialogService.closeDialog(this.itemRejectedConfirmDialogName);
        this._dialogService.closeDialog(this.itemIncompleteConfirmDialogName);
    }

    private validateFields(): boolean {
        // A field the user never touched has never validated itself, so without this a missing
        // required value would pass unnoticed.
        this._validationRegistry.runValidators();

        if (this._validationRegistry.hasAnyErrors()) {
            this._toastrService.warning(
                this._validationRegistry
                    .allMessages()
                    .map((e) => "- " + e)
                    .join("\n"),
                "Save failed",
            );
            return false;
        }
        return true;
    }

    private async save(): Promise<void> {
        await firstValueFrom(
            this._processedDocumentsService.updateDocument(+this.id, this.risks()),
        );
    }

    private async handleInterval(): Promise<void> {
        const processedDocument = await this.loadProcessedDocument(+this.id);

        if (this.isCompletedWorkflowStatus(processedDocument.workflowStatus)) {
            clearInterval(this._waitingTimeInterval);
            this.processedDocument = processedDocument;
            this.setCompletedRisksAndControls(this.processedDocument);
            this.setWorkflowStep(this.workflowComplete);
            this.showAllControls();
            return;
        }

        const remainingTime = this.calculateRemainingTime(processedDocument);

        if (remainingTime == "Finished Processing") {
            clearInterval(this._waitingTimeInterval);
            this.processedDocument = processedDocument;
            this.risks.set(this.processedDocument.risks);
            this.setWorkflowStep(this.workflowSelect);
            this.showAllControls();
        } else {
            this.creaiLoadingMinutes.update((_) => remainingTime);
        }
    }

    private updateCompletedWithoutOutputState(): void {
        this.completedWithoutOutput =
            this.currentStepId() === this.workflowComplete && this.risks().length === 0;
    }

    private async loadProcessedDocument(id: number): Promise<ProcessedDocumentDetailModel> {
        const processedDocument = await firstValueFrom(
            this._processedDocumentsService.getDetail(id),
        );
        return processedDocument;
    }

    private isCompletedWorkflowStatus(status: ProcessedDocumentWorkflowStatus): boolean {
        return (
            status == ProcessedDocumentWorkflowStatus.completed ||
            status == ProcessedDocumentWorkflowStatus.completedNoSuggestions
        );
    }

    private calculateRemainingTime(processedDocument: ProcessedDocumentDetailModel): string {
        if (processedDocument.chunks.length === 0) {
            return "Still estimating the remaining time";
        }

        const startTime = processedDocument.document.date;
        const currentTime = new Date();
        const elapsedTime = currentTime.getTime() - startTime.getTime();

        const chunks = processedDocument.chunks;
        const finishedCount = chunks.filter((chunk) => chunk.isDoneProcessing).length;
        const finishedFraction = finishedCount / chunks.length;

        if (finishedFraction === 1) {
            return "Finished Processing";
        }

        if (finishedFraction === 0) {
            return "Still estimating the remaining time";
        }

        const totalExpectedTime = elapsedTime / finishedFraction;
        const remainingTimeInMs = totalExpectedTime - elapsedTime;

        // More than a day
        if (remainingTimeInMs > 86400000) {
            return "Failed estimating the remaining time";
        }

        const remainingTime = new Date(remainingTimeInMs);

        const remainingHours = remainingTime.getUTCHours();
        if (remainingHours === 1) {
            return "1 hour remaining...";
        }
        if (remainingHours > 0) {
            return remainingHours + " hours remaining...";
        }

        const remainingMinutes = remainingTime.getUTCMinutes();

        if (remainingMinutes <= 1) {
            return "1 minute remaining...";
        }

        return remainingMinutes + " minutes remaining...";
    }

    private setWorkflowStep(workflowStepId: string) {
        switch (workflowStepId) {
            case this.workflowGenerate:
                this.currentStepId.set(this.workflowGenerate);
                break;
            case this.workflowSelect:
                this.workflowSteps.find((step) => step.id === this.workflowGenerate)!.status =
                    WorkflowStepStatus.Completed;
                this.workflowSteps.find((step) => step.id === this.workflowSelect)!.status =
                    WorkflowStepStatus.Active;
                this.currentStepId.set(this.workflowSelect);
                break;
            case this.workflowRefine:
                this.workflowSteps.find((step) => step.id === this.workflowGenerate)!.status =
                    WorkflowStepStatus.Completed;
                this.workflowSteps.find((step) => step.id === this.workflowSelect)!.status =
                    WorkflowStepStatus.Completed;
                this.workflowSteps.find((step) => step.id === this.workflowRefine)!.status =
                    WorkflowStepStatus.Active;
                this.currentStepId.set(this.workflowRefine);
                break;
            case this.workflowComplete:
                this.workflowSteps.find((step) => step.id === this.workflowGenerate)!.status =
                    WorkflowStepStatus.Completed;
                this.workflowSteps.find((step) => step.id === this.workflowSelect)!.status =
                    WorkflowStepStatus.Completed;
                this.workflowSteps.find((step) => step.id === this.workflowRefine)!.status =
                    WorkflowStepStatus.Completed;
                this.workflowSteps.find((step) => step.id === this.workflowComplete)!.status =
                    WorkflowStepStatus.Completed;
                this.currentStepId.set(this.workflowComplete);
                break;
        }

        this.updateCompletedWithoutOutputState();
    }

    public openNewRisk(suggestedRiskId: number) {
        const suggestedRisk = this.risks().find((r) => r.id === suggestedRiskId);

        this.openNewRiskTab(suggestedRisk);
    }

    private openNewRiskTab(suggestedRisk: ProcessedDocumentSuggestionModel) {
        const config = <RiskConfigModel>{
            name: suggestedRisk.name,
            description: suggestedRisk.description,
            processedDocumentRiskId: suggestedRisk.id,
        };

        this._tabService.generateTab(this._pages.RiskDetail, 0, "(R) New Risk", config);
    }

    public openNewControl(suggestedControlId: number) {
        const suggestedControl = this.risks()
            .flatMap((r) => r.controls)
            .find((c) => c.id == suggestedControlId);

        this.openNewControlTab(suggestedControl);
    }

    private openNewControlTab(suggestedControl: ProcessedDocumentSuggestionModel) {
        const config = <ControlConfigModel>{
            name: suggestedControl.name,
            description: suggestedControl.description,
            processedDocumentControlId: suggestedControl.id,
        };

        this._tabService.generateTab(this._pages.ControlDetail, 0, "(C) New Control", config);
    }

    public openCreatedRisk(suggestedRisk: ProcessedDocumentSuggestionModel) {
        var riskId = suggestedRisk.linkedGuid;
        this._tabService.generateTab(this._pages.RiskDetail, riskId);
    }

    public openCreatedControl(suggestedControl: ProcessedDocumentSuggestionModel) {
        var controlId = suggestedControl.linkedGuid;
        this._tabService.generateTab(this._pages.ControlDetail, controlId);
    }
}
